// @ts-nocheck
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Loader2, Search, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

interface Props {
  eventId?: string;
}

export function SignedWaiversPanel({ eventId }: Props) {
  const [search, setSearch] = useState("");

  const { data: signatures = [], isLoading } = useQuery({
    queryKey: ["event-signed-waivers", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("event_waiver_signatures")
        .select("*")
        .eq("event_id", eventId)
        .order("signed_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const { teamSigs, indivSigs } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? signatures.filter((s: any) =>
          [s.signer_name, s.signer_email, s.athlete_name, s.signer_relationship]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q))
        )
      : signatures;
    return {
      teamSigs: filtered.filter((s: any) => s.waiver_type === "team"),
      indivSigs: filtered.filter((s: any) => s.waiver_type !== "team"),
    };
  }, [signatures, search]);

  const exportCsv = () => {
    if (signatures.length === 0) {
      toast.error("No waiver signatures to export");
      return;
    }
    const headers = ["Type", "Athlete", "Signer", "Email", "Relationship", "Signed At", "Method"];
    const rows = signatures.map((s: any) => [
      s.waiver_type || "individual",
      s.athlete_name || "",
      s.signer_name || "",
      s.signer_email || "",
      s.signer_relationship || "",
      s.signed_at ? new Date(s.signed_at).toLocaleString() : "",
      s.signature_method || "",
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `signed-waivers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-lg border bg-card p-4 space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" /> Signed Waivers
          </h2>
          <p className="text-sm text-muted-foreground">Waiver signatures collected during registration.</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={isLoading || signatures.length === 0}>
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search signer, athlete, or email..."
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : (
        <Tabs defaultValue="team">
          <TabsList>
            <TabsTrigger value="team">Team ({teamSigs.length})</TabsTrigger>
            <TabsTrigger value="individual">Individual ({indivSigs.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="team"><SignatureRows rows={teamSigs} type="team" /></TabsContent>
          <TabsContent value="individual"><SignatureRows rows={indivSigs} type="individual" /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function SignatureRows({ rows, type }: { rows: any[]; type: "team" | "individual" }) {
  if (rows.length === 0) {
    return <p className="py-8 text-center text-sm italic text-muted-foreground">No signatures yet.</p>;
  }

  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted text-left text-muted-foreground">
          <tr>
            {type === "individual" && <th className="px-3 py-2 font-medium">Athlete</th>}
            <th className="px-3 py-2 font-medium">Signer</th>
            <th className="px-3 py-2 font-medium">Email</th>
            <th className="px-3 py-2 font-medium">Signed</th>
            <th className="px-3 py-2 font-medium">Method</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r: any) => (
            <tr key={r.id} className="border-t">
              {type === "individual" && <td className="px-3 py-2">{r.athlete_name || "—"}</td>}
              <td className="px-3 py-2 font-medium">{r.signer_name || "—"}</td>
              <td className="px-3 py-2 text-muted-foreground">{r.signer_email || "—"}</td>
              <td className="px-3 py-2 text-muted-foreground">
                {r.signed_at ? new Date(r.signed_at).toLocaleDateString() : "—"}
              </td>
              <td className="px-3 py-2 text-muted-foreground">{r.signature_method || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}